import React, {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { useDisclosure, useToast } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { fetchArts } from 'api/art';
import { fetchManufacturers } from 'api/manufacturer';
import { fetchWashHubLocations } from 'api/location';
import { fetchWashHubAdminsForLocation } from 'api/employees';
import { fetchSkusByManufacturer } from 'api/sku';
import { useUser } from 'store/slices/auth/authSelectors';

import {
  CreatePurchaseOrderModalProps,
  RefType,
} from './CreatePurchaseOrderModal.props';
import CreatePurchaseOrderModalView from './CreatePurchaseOrderModal.view';
import { createPurchaseOrderFromForm } from './createPurchaseOrder';
import {
  CreatePurchaseOrderFormValues,
  getShipToContactDetails,
} from './CreatePurchaseOrderModal.utils';

const Container = forwardRef<RefType, CreatePurchaseOrderModalProps>(
  (_, ref) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const queryClient = useQueryClient();
    const [selectedWashHubId, setSelectedWashHubId] = useState('');
    const [formResetKey, setFormResetKey] = useState(0);

    const currentUser = useUser() as {
      fname?: string;
      lname?: string;
      firstName?: string;
      lastName?: string;
    };

    const {
      data: manufacturers = [],
      isLoading: isLoadingManufacturers,
    } = useQuery(['fetch-manufacturers'], () => fetchManufacturers(), {
      enabled: isOpen,
    });

    const { data: washHubs = [], isLoading: isLoadingWashHubs } = useQuery(
      ['fetch-wash-hub-locations'],
      fetchWashHubLocations,
      { enabled: isOpen },
    );

    const { data: arts = [], isLoading: isLoadingArts } = useQuery(
      ['fetch-arts'],
      fetchArts,
      { enabled: isOpen },
    );

    const {
      data: washHubAdmins = [],
      isLoading: isLoadingWashHubAdmins,
    } = useQuery(
      ['fetch-wash-hub-admins', selectedWashHubId],
      () => fetchWashHubAdminsForLocation(selectedWashHubId),
      { enabled: isOpen && Boolean(selectedWashHubId) },
    );

    const handleClose = () => {
      setSelectedWashHubId('');
      onClose();
    };

    const {
      mutateAsync: createPurchaseOrder,
      isLoading: isCreatingPurchaseOrder,
    } = useMutation(
      async (values: CreatePurchaseOrderFormValues) => {
        const manufacturer = manufacturers.find(
          (item) => item._id === values.manufacturerId,
        );
        const washHub = washHubs.find(
          (item) => item._id === values.destinationHubId,
        );
        const shipToContactDetails = getShipToContactDetails(
          values,
          washHubAdmins,
        );
        const skus = await fetchSkusByManufacturer(values.manufacturerId);

        return createPurchaseOrderFromForm(
          values,
          {
            manufacturerName: manufacturer?.name ?? '',
            destinationHubName: values.isManualDestinationHub
              ? values.manualDestinationHubName
              : washHub?.name ?? '',
            shipToContact: shipToContactDetails.name,
            shipToContactPhone: shipToContactDetails.phone,
            shipToContactEmail: shipToContactDetails.email,
          },
          skus,
        );
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['fetch-purchase-orders']);
          toast({
            status: 'success',
            title: 'Purchase order created',
            description: 'The PDF was uploaded and downloaded locally.',
            isClosable: true,
            duration: 4000,
          });
          handleClose();
        },
        onError: (error: Error) => {
          toast({
            status: 'error',
            title: 'Failed to create purchase order',
            description: error?.message || 'Something went wrong.',
            isClosable: true,
            duration: 5000,
          });
        },
      },
    );

    useImperativeHandle(
      ref,
      () => ({
        onOpen: () => {
          setSelectedWashHubId('');
          setFormResetKey((key) => key + 1);
          onOpen();
        },
      }),
      [onOpen],
    );

    const handleCreatePurchaseOrder = useCallback(
      async (values: CreatePurchaseOrderFormValues) => {
        await createPurchaseOrder(values);
      },
      [createPurchaseOrder],
    );

    return (
      <CreatePurchaseOrderModalView
        formResetKey={formResetKey}
        isOpen={isOpen}
        onClose={handleClose}
        manufacturers={manufacturers}
        washHubs={washHubs}
        arts={arts}
        washHubAdmins={washHubAdmins}
        isLoadingManufacturers={isLoadingManufacturers}
        isLoadingWashHubs={isLoadingWashHubs}
        isLoadingArts={isLoadingArts}
        isLoadingWashHubAdmins={isLoadingWashHubAdmins}
        currentUser={currentUser}
        onDestinationHubChange={setSelectedWashHubId}
        onCreatePurchaseOrder={handleCreatePurchaseOrder}
        isCreatingPurchaseOrder={isCreatingPurchaseOrder}
      />
    );
  },
);

const CreatePurchaseOrderModal = memo(Container);

export default CreatePurchaseOrderModal;
